/**
 * Technikal CHROME, DROP 001: behaviour for the "Build yours" configurator (sections/tkc-configurator.liquid on the
 * homepage, blocks/tkc-configurator.liquid on the product page), the fit checker (blocks/tkc-fit-check.liquid),
 * the designs gallery and the slow scroll reveal.
 *
 * <tkc-configurator> (markup: snippets/tkc-config-steps / tkc-config-stage / tkc-config-bar)
 *  - Stepper 01 model -> 02 colour (depends on the model) -> 03 finish -> 04 design. Phones: accordion (current step
 *    open, finished steps collapse to a chip in their header; tap to change). Progress line = finished steps.
 *  - Render = `{prefix}{design}-{finish}-{colour}{ext}` on the Shopify Files base URL rendered by Liquid.
 *    Missing file -> `{design}-chrome-silver-midnight` -> theme placeholder (data-placeholder on the design).
 *    Loaded + decoded before a short cross-fade (none with reduced motion); neighbours (the other finishes and
 *    designs for this colour) are preloaded when the browser is idle. Tap to zoom (lightbox); swipe = next design.
 *  - Deep link ?airpods=max2|max1&colour=…&finish=…&design=… (each change rewrites them with replaceState, other
 *    params stay); the last choice is remembered in localStorage and restored after a waitlist post.
 *  - Waitlist tags (every [data-tkc-tags] in the same Shopify section):
 *    drop-001,waitlist,design-…,finish-…,airpods-model-…,airpods-colour-…
 *  - Product mode (data-mode="product"): design = this product (data-design); finish radios drive Horizon's variant
 *    picker (its own radios + change event) and follow it back after `shopify:product:select`; model + colour go
 *    into the hidden line item properties [data-tkc-prop]; design tiles link to the other products with the choice.
 *    The render also replaces the first product photo (desktop) - never with a placeholder.
 *  - Phone sticky bar (thumbnail + "DESIGN · Finish · €89" + button) once all four steps are answered and the form /
 *    Add to cart is off screen; "Share your build" (navigator.share on touch
 *    devices, else clipboard + toast), aria-live summary.
 * Designs gallery: [data-tkc-pick] links ("?design=x#configure") work without JS; with JS they dispatch `tkc:select`.
 * Loaded as a module by each section/block; modules run once even when included several times.
 */

// Bump when configurator photos in Shopify Files are replaced in place: the CDN caches each URL for a year.
const IMG_VER = '5';

const LAST_KEY = 'tkc-drop001-last';
const POST_KEY = 'tkc-drop001-choice';
const PARAMS = ['airpods', 'colour', 'finish', 'design'];
const ALL_OPEN = '*';
const STEPS = ['model', 'colour', 'finish', 'design'];
const ALIASES = {
  'space-gray': 'spacegray', 'space-grey': 'spacegray', spacegrey: 'spacegray',
  'fuck-off': 'fuckoff', 'f-ck-off': 'fuckoff', 'fck-off': 'fuckoff',
  gold: 'champagne-gold', champagne: 'champagne-gold', chrome: 'chrome-silver',
  // models
  'max-2': 'max2', 'airpods-max-2': 'max2', 'usb-c': 'max2', usbc: 'max2', '2024': 'max2',
  max: 'max1', 'max-1': 'max1', 'airpods-max': 'max1', lightning: 'max1', '2020': 'max1', max2020: 'max1',
};
const FALLBACK_FINISH = 'chrome-silver';
const FALLBACK_COLOUR = 'midnight';

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const isPhone = () => window.matchMedia?.('(max-width: 749px)').matches;
const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 300));
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

/** @param {unknown} value */
export function normalise(value) {
  const s = String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return ALIASES[s] ?? s;
}

/** Reads ?airpods= &colour= (or &color=) &finish= &design= from the page URL. */
export function readParams(search = window.location.search) {
  const p = new URLSearchParams(search);
  return {
    airpods: normalise(p.get('airpods')),
    colour: normalise(p.get('colour') ?? p.get('color')),
    finish: normalise(p.get('finish')),
    design: normalise(p.get('design')),
  };
}

const track = (name, data) => {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch { /* editor / no pixel */ }
};

const store = (area, key) => ({
  get() { try { return JSON.parse(window[area].getItem(key) || 'null'); } catch { return null; } },
  set(v) { try { window[area].setItem(key, JSON.stringify(v)); } catch { /* private mode / blocked */ } },
});
const lastChoice = store('localStorage', LAST_KEY);
const postedChoice = store('sessionStorage', POST_KEY);

/** Remembers which file names exist so a known-missing file is never requested twice. */
const exists = new Map();

/** Loads an image URL; resolves true when it decodes, false on error. */
function probe(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img.naturalWidth > 0);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

class TkcConfigurator extends HTMLElement {
  /** @type {AbortController | undefined} */ #ac;
  #token = 0;
  #thumbToken = 0;
  #galleryUrl = '';
  #currentUrl = '';
  #swiped = false;
  /** @type {Set<string>} steps the visitor has answered (or that arrived answered via a link) */
  #done = new Set();

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.product = this.dataset.mode === 'product';
    this.scope = this.closest('.shopify-section') || document;
    this.base = this.dataset.fileBase || '';
    this.filePrefix = this.dataset.filePrefix || '';
    this.ext = this.dataset.fileExt || '.jpg';
    this.cdnWidth = this.dataset.cdnWidth === 'true';
    this.frame = this.querySelector('[data-tkc-frame]');
    this.imgs = [...this.querySelectorAll('[data-tkc-img]')];
    this.stepsEl = this.querySelector('[data-tkc-steps]');
    this.stepsEl?.setAttribute('data-js', '');

    // The server already renders the default render: reuse its load result instead of probing it again.
    const first = this.imgs[0];
    this.firstName = first?.dataset.name || '';
    this.firstOk = !first?.getAttribute('src')
      ? Promise.resolve(false)
      : first.complete
        ? Promise.resolve(first.naturalWidth > 0)
        : new Promise((resolve) => {
          first.addEventListener('load', () => resolve(true), { once: true, signal });
          first.addEventListener('error', () => resolve(false), { once: true, signal });
        });

    if (this.product && !this.galleryImage()) this.setAttribute('data-no-gallery', '');

    // Start state: URL params > the choice posted with the waitlist form > the last choice on this device > defaults.
    const fromUrl = readParams();
    const done = this.scope.querySelector('[data-tkc-done]');
    const failed = this.scope.querySelector('[data-tkc-waitlist] [role="alert"]');
    let start = null;
    if (PARAMS.some((k) => fromUrl[k])) start = fromUrl;
    else if (done || failed) start = postedChoice.get();
    else start = lastChoice.get();
    if (start) {
      this.apply(start);
      STEPS.forEach((s) => this.#done.add(s)); // arrived with a complete choice: every step shows its chip
    }
    // Product page: every product IS one design (no design step), so the design counts as chosen from the start
    if (this.product || !this.querySelector('[data-tkc-step="design"]')) this.#done.add('design');
    this.syncModel();
    if (this.product) this.pushFinishToPicker();
    this.openStep(ALL_OPEN);
    this.update({ silent: true, initial: true });

    this.addEventListener('change', (e) => this.onChange(e), { signal });
    this.addEventListener('click', (e) => this.onClick(e), { signal });
    this.addEventListener('pointerover', (e) => this.onPreload(e), { signal, passive: true });
    this.addEventListener('focusin', (e) => this.onPreload(e), { signal });
    this.setupSwipe(signal);
    this.setupStickyBar(signal);
    document.addEventListener('tkc:select', (e) => this.onSelect(/** @type {CustomEvent} */ (e)), { signal });
    // Waitlist form(s) in this section: final tag sync + remember the choice for after the redirect.
    this.scope.addEventListener('submit', (e) => {
      if (!(e.target instanceof HTMLFormElement) || !e.target.matches('[data-tkc-form]')) return;
      this.syncTags();
      postedChoice.set(this.params);
      track('tkc_waitlist_submit', this.state);
    }, { signal, capture: true });
    if (this.product) {
      document.addEventListener('shopify:product:select', (e) => this.onProductSelect(e), { signal });
    }
    if (done) requestAnimationFrame(() => /** @type {HTMLElement} */ (done).focus({ preventScroll: true }));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- state ---------- */

  /** @param {'model'|'colour'|'finish'|'design'} group */
  radios(group) {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll(`input[data-tkc-group="${group}"]`)]);
  }

  checked(group) {
    return this.radios(group).find((r) => r.checked);
  }

  /** The design's element (radio on the homepage, link on the product page): name + placeholder live there. */
  designEl(design) {
    return this.radios('design').find((r) => r.value === design)
      || this.querySelector(`[data-tkc-design-link="${CSS.escape(design)}"]`);
  }

  get designKeys() {
    const radios = this.radios('design').map((r) => r.value);
    return radios.length ? radios : [...this.querySelectorAll('[data-tkc-design-link]')].map((a) => a.getAttribute('data-tkc-design-link') || '');
  }

  get state() {
    return {
      model: this.checked('model')?.value || 'max2',
      colour: this.checked('colour')?.value || '',
      finish: this.checked('finish')?.value || '',
      design: this.checked('design')?.value || this.dataset.design || '',
    };
  }

  /** The choice as URL params. */
  get params() {
    const { model, colour, finish, design } = this.state;
    return { airpods: model, colour, finish, design };
  }

  get names() {
    const s = this.state;
    const model = this.checked('model');
    return {
      model: model?.dataset.name || s.model,
      short: model?.dataset.short || 'AirPods Max',
      colour: this.checked('colour')?.dataset.name || s.colour,
      hex: this.checked('colour')?.dataset.hex || '',
      finish: this.checked('finish')?.dataset.name || s.finish,
      design: this.designEl(s.design)?.dataset.name || this.dataset.designName || s.design,
    };
  }

  /** Checks the radios named in `partial` (airpods/model, colour, finish, design); unknown values are ignored. */
  apply(partial = {}) {
    const wanted = {
      model: normalise(partial.airpods ?? partial.model),
      colour: normalise(partial.colour),
      finish: normalise(partial.finish),
      design: this.product ? '' : normalise(partial.design),
    };
    // A colour that only exists for one model decides the model.
    const colourRadio = this.radios('colour').find((r) => r.value === wanted.colour);
    if (colourRadio && !wanted.model) wanted.model = colourRadio.dataset.model || '';
    for (const group of /** @type {const} */ (['model', 'finish', 'design'])) {
      const radio = this.radios(group).find((r) => r.value === wanted[group]);
      if (radio) radio.checked = true;
    }
    this.syncModel();
    if (colourRadio && !colourRadio.disabled) colourRadio.checked = true;
  }

  /** Only the chosen model's colours are visible/enabled; keeps the colour valid for that model. */
  syncModel() {
    const model = this.checked('model')?.value || 'max2';
    for (const radio of this.radios('colour')) {
      const own = radio.dataset.model === model;
      radio.disabled = !own;
      radio.hidden = !own;
      const label = this.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      if (label) /** @type {HTMLElement} */ (label).hidden = !own;
    }
    const colour = this.checked('colour');
    if (!colour || colour.disabled) {
      const first = this.radios('colour').find((r) => !r.disabled);
      if (first) first.checked = true;
    }
  }

  /* ---------- stepper ---------- */

  stepOf(input) {
    return input.closest('[data-tkc-step]')?.getAttribute('data-tkc-step') || '';
  }

  /** Opens one step (null = none) and refreshes done marks and the progress line. */
  openStep(name) {
    if (!this.stepsEl) return;
    for (const li of this.stepsEl.querySelectorAll('[data-tkc-step]')) {
      const key = li.getAttribute('data-tkc-step') || '';
      const open = name === ALL_OPEN || key === name;
      li.classList.toggle('is-open', open);
      li.classList.toggle('is-done', this.#done.has(key));
      li.querySelector('[data-tkc-step-head]')?.setAttribute('aria-expanded', String(open || !isPhone()));
    }
    // progress over the steps that are actually shown (3 on a product page, 4 on the homepage)
    const shown = [...this.stepsEl.querySelectorAll('[data-tkc-step]')].map((li) => li.getAttribute('data-tkc-step') || '');
    const gaps = Math.max(shown.length - 1, 1);
    this.stepsEl.style.setProperty('--tkc-progress', String(Math.min(shown.filter((k) => this.#done.has(k)).length, gaps) / gaps));
    this.renderSticky?.();
  }

  /** After an answer: mark the step done (progress line + summary chip). Steps never collapse or scroll on their
   *  own any more: the owner saw the page jump and the picked card vanish, so all steps stay open and the
   *  selection (✓) stays in view. */
  advance(step) {
    this.#done.add(step);
    this.openStep(ALL_OPEN);
  }

  /* ---------- events ---------- */

  #lastChange = 0;

  onChange(event) {
    const input = event.target;
    // Step 04 'Protect it' (product page): covers only / covers + THE VAULT -> waitlist tag addon-case
    if (input instanceof HTMLInputElement && input.matches('[data-tkc-case-opt]')) {
      const chip = this.querySelector('[data-tkc-chip="protect"]');
      if (chip) chip.textContent = input.dataset.name || '';
      this.advance('protect');
      this.syncTags();
      return;
    }
    if (!(input instanceof HTMLInputElement) || !input.dataset.tkcGroup) return;
    this.#lastChange = performance.now();
    const group = input.dataset.tkcGroup;
    if (group === 'model') this.syncModel();
    if (group === 'finish' && this.product) this.pushFinishToPicker();
    this.advance(this.stepOf(input));
    this.update();
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    // Tapping the option that is already selected (e.g. the default model) still counts as an answer.
    if (target instanceof HTMLInputElement && target.dataset.tkcGroup && target.checked) {
      const at = performance.now();
      setTimeout(() => { if (this.#lastChange < at) this.advance(this.stepOf(target)); }, 0);
      return;
    }
    const head = target.closest('[data-tkc-step-head]');
    if (head && isPhone()) {
      const li = head.closest('[data-tkc-step]');
      this.openStep(ALL_OPEN); // steps stay open; tapping a header no longer folds them
      return;
    }
    if (target.closest('[data-tkc-lightbox-close]') || target.matches('[data-tkc-lightbox], [data-tkc-lightbox-img]')) {
      /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-tkc-lightbox]'))?.close();
      return;
    }
    if (target.closest('[data-tkc-zoom]') || target.matches('[data-tkc-img].is-active')) {
      if (!this.#swiped) this.openLightbox();
      return;
    }
    if (target.closest('[data-tkc-share]')) {
      this.share();
      return;
    }
    if (target.closest('[data-tkc-sticky-btn]')) this.stickyAction();
  }

  /** From the designs gallery or the fit checker: { airpods?, colour?, finish?, design? }. */
  onSelect(event) {
    const detail = event.detail || {};
    if (this.product && detail.design && !detail.airpods && !detail.colour) return;
    this.apply(detail);
    if (detail.airpods || detail.colour) { this.#done.add('model'); this.#done.add('colour'); }
    if (detail.design) this.#done.add('design');
    if (this.product) this.pushFinishToPicker();
    this.openStep(ALL_OPEN);
    this.update();
  }

  /* ---------- Horizon variant picker (product page) ---------- */

  /** Horizon's radio for an option value in this section, e.g. ('Finish', 'Champagne Gold'). */
  pickerInput(optionName, value) {
    return /** @type {HTMLInputElement[]} */ ([...this.scope.querySelectorAll('variant-picker input[type="radio"]')])
      .find((i) => i.dataset.optionName === optionName && i.value === value);
  }

  /** Our finish -> Horizon: check its radio and fire `change` so Horizon loads that variant. */
  #finishAt = 0;
  pushFinishToPicker() {
    // Waitlist mode (nothing to buy): don't drive Horizon's variant picker. Its section re-render (fetch + morph)
    // used to land after a quick second tap and reset the new finish (owner: 'choice doesn't stick').
    if (this.scope.querySelector('[data-tkc-waitlist]')) return;
    this.#finishAt = performance.now();
    const radio = this.checked('finish');
    const { optionName, optionValue } = radio?.dataset || {};
    if (!optionName || !optionValue) return;
    const input = this.pickerInput(optionName, optionValue);
    if (!input || input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Horizon -> our finish (after the picker was morphed), then re-render (the gallery was replaced too). */
  onProductSelect(event) {
    if (!(event.target instanceof Element) || !this.scope.contains(event.target) || event.target.closest('product-card')) return;
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    Promise.resolve(promise).then(async () => {
      await nextFrame(); // media-gallery replaces itself in the same tick
      if (performance.now() - this.#finishAt < 2500) {
        // the visitor picked a finish here a moment ago: their choice wins over a late re-render
        this.pushFinishToPicker();
      } else {
        for (const radio of this.radios('finish')) {
          const input = radio.dataset.optionName ? this.pickerInput(radio.dataset.optionName, radio.dataset.optionValue || '') : null;
          if (input?.checked) radio.checked = true;
        }
      }
      this.#galleryUrl = '';
      this.toggleAttribute('data-no-gallery', !this.galleryImage());
      this.update();
    }).catch(() => {});
  }

  /* ---------- images ---------- */

  /** Warm the cache for the combination under the pointer / focus. */
  onPreload(event) {
    const label = event.target instanceof Element ? event.target.closest('label[for]') : null;
    const input = label ? this.querySelector(`#${CSS.escape(label.htmlFor)}`) : event.target;
    if (!(input instanceof HTMLInputElement) || !['colour', 'finish', 'design'].includes(input.dataset.tkcGroup || '')) return;
    const next = { ...this.state, [input.dataset.tkcGroup || '']: input.value };
    this.warm(this.fileName(next.design, next.finish, next.colour));
  }

  warm(name) {
    if (!this.base || exists.get(name) === false) return;
    probe(this.url(name, this.width())).then((ok) => exists.set(name, ok));
  }

  /** Neighbours of the current choice: the other finishes and designs for this colour. */
  preloadNeighbours({ design, finish, colour }) {
    idle(() => {
      for (const f of this.radios('finish').map((r) => r.value)) if (f !== finish) this.warm(this.fileName(design, f, colour));
      for (const d of this.designKeys) if (d !== design) this.warm(this.fileName(d, finish, colour));
    });
  }

  fileName(design, finish, colour) {
    return `${this.filePrefix}${design}-${finish}-${colour}`;
  }

  url(name, width) {
    const sep = this.base.includes('?') ? '&' : '?';
    return `${this.base}${name}${this.ext}${sep}v=${IMG_VER}${this.cdnWidth && width ? `&width=${width}` : ''}`;
  }

  visibleFrame() {
    return this.frame && this.frame.offsetParent !== null ? this.frame : null;
  }

  /** Requested render width: frame width x pixel ratio, rounded up to 200 px steps (CDN cache friendly). */
  width(cssWidth = (this.visibleFrame() || this.galleryImage())?.clientWidth || 600) {
    const px = cssWidth * Math.min(window.devicePixelRatio || 1, 2);
    return Math.min(1600, Math.max(400, Math.ceil(px / 200) * 200));
  }

  /**
   * First image that loads: exact render, then the fallbacks, then the theme placeholder.
   * @returns {Promise<{ name: string, url: string }>}
   */
  async resolve(design, finish, colour, width, fallbacks = [[FALLBACK_FINISH, FALLBACK_COLOUR]]) {
    const names = [this.fileName(design, finish, colour), ...fallbacks.map(([f, c]) => this.fileName(design, f, c))];
    if (this.base) {
      for (const name of new Set(names)) {
        if (exists.get(name) === false) continue;
        const url = this.url(name, width);
        const ok = exists.get(name) ?? (name === this.firstName ? await this.firstOk : await probe(url));
        exists.set(name, ok);
        if (ok) return { name, url };
      }
    }
    return { name: `placeholder:${design}`, url: this.designEl(design)?.dataset.placeholder || '' };
  }

  /** Product page: the first product photo (null when the product has none or the preview is "block"). */
  galleryImage() {
    if (!this.product || this.dataset.preview === 'block') return null;
    const gallery = this.scope.querySelector('media-gallery');
    return /** @type {HTMLImageElement | null} */ (gallery?.querySelector('slideshow-slide img, .product-media img, img') || null);
  }

  /* ---------- sync ---------- */

  syncTags() {
    const { model, colour, finish, design } = this.state;
    for (const tags of this.scope.querySelectorAll('[data-tkc-tags]')) {
      // THE VAULT: step 04 'Protect it' in the configurator (or an old tick box in the form) adds tag addon-case
      const pick = /** @type {HTMLInputElement | null} */ (this.scope.querySelector('[data-tkc-case-opt]:checked') || tags.closest('form')?.querySelector('[data-tkc-case-opt]:checked'));
      const withCase = pick?.value === 'addon-case';
      /** @type {HTMLInputElement} */ (tags).value = `drop-001,waitlist,design-${design},finish-${finish},airpods-model-${model},airpods-colour-${colour}${withCase ? ',addon-case' : ''}`;
    }
  }

  /** The current choice as a shareable URL (product page: this product; else the page + #configure). */
  shareUrl() {
    const url = new URL(window.location.href);
    for (const [k, v] of Object.entries(this.params)) {
      if (this.product && k === 'design') url.searchParams.delete(k);
      else url.searchParams.set(k, v);
    }
    url.searchParams.delete('color');
    url.hash = this.product ? '' : (this.closest('section')?.id || 'configure');
    return url;
  }

  syncUrl() {
    try {
      const url = this.shareUrl();
      url.hash = window.location.hash;
      window.history.replaceState(window.history.state, '', url);
    } catch { /* sandboxed preview */ }
  }

  /** Design links + "shop this pair" carry the current choice. */
  syncLinks() {
    const { airpods, colour, finish } = this.params;
    const withChoice = (href) => {
      try {
        const u = new URL(href, window.location.origin);
        u.searchParams.set('airpods', airpods);
        u.searchParams.set('colour', colour);
        u.searchParams.set('finish', finish);
        return u.pathname + u.search;
      } catch { return href; }
    };
    for (const link of this.querySelectorAll('a[data-tkc-design-link]')) {
      link.setAttribute('href', withChoice(link.getAttribute('href') || ''));
    }
    const shop = this.querySelector('a[data-tkc-shop]');
    const url = this.designEl(this.state.design)?.dataset.url;
    if (shop && url) shop.setAttribute('href', withChoice(url));
  }

  syncText() {
    const n = this.names;
    const summary = this.querySelector('[data-tkc-summary]');
    const text = `${n.design} · ${n.finish} · for ${n.colour} ${n.short}`;
    if (summary && summary.textContent !== text) summary.textContent = text;
    const caption = this.querySelector('[data-tkc-caption]');
    if (caption) caption.textContent = `Your ${n.short} · ${n.colour} — ${n.design} in ${n.finish}`;
    const code = this.querySelector('[data-tkc-code]');
    if (code) code.textContent = `${n.design} / ${n.finish}`;
    this.alt = `${n.design} chrome covers in ${n.finish} on ${n.colour} ${n.short}`;
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] || c));
    const chip = (key, html) => { const el = this.querySelector(`[data-tkc-chip="${key}"]`); if (el) el.innerHTML = html; };
    chip('model', esc(this.checked('model')?.dataset.chip || n.model));
    chip('colour', `<span class="tkc-step__dot" style="--tkc-dot: ${esc(n.hex)}"></span>${esc(n.colour)}`);
    chip('finish', esc(n.finish));
    chip('design', esc(n.design));
    for (const prop of this.querySelectorAll('[data-tkc-prop]')) {
      /** @type {HTMLInputElement} */ (prop).value = prop.getAttribute('data-tkc-prop') === 'model' ? n.model : n.colour;
    }
    const bar = this.querySelector('[data-tkc-sticky-text]');
    const price = this.querySelector('[data-tkc-sticky]')?.getAttribute('data-price') || '';
    if (bar) bar.textContent = [n.design, n.finish, price].filter(Boolean).join(' · ');
  }

  /** Re-renders text, tags, properties, links, URL, thumbnails and the big image for the current choice. */
  update({ silent = false, initial = false } = {}) {
    const s = this.state;
    this.syncText();
    this.syncTags();
    this.syncLinks();
    if (!silent) {
      this.syncUrl();
      lastChoice.set(this.params);
      track('tkc_configure', s);
    }
    this.swap(s, initial);
    this.thumbs(s);
  }

  async swap({ design, finish, colour }, initial) {
    const token = ++this.#token;
    const { name, url } = await this.resolve(design, finish, colour, this.width());
    if (token !== this.#token || !url) return;
    this.#currentUrl = url;
    const thumb = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-tkc-sticky-thumb]'));
    if (thumb) thumb.src = url;
    this.preloadNeighbours({ design, finish, colour });

    // Product photo #1 becomes the render (Horizon re-creates the gallery on variant change; we re-apply then).
    // Never replace a real product photo with a placeholder.
    const gallery = this.galleryImage();
    if (gallery && !name.startsWith('placeholder:') && !(this.#galleryUrl === url && gallery.getAttribute('src') === url)) {
      const img = new Image();
      img.src = url;
      img.decode().catch(() => {}).then(() => {
        if (token !== this.#token) return;
        gallery.removeAttribute('srcset');
        gallery.removeAttribute('sizes');
        gallery.src = url;
        gallery.alt = this.alt;
        this.#galleryUrl = url;
        if (!initial) {
          try { /** @type {any} */ (this.scope.querySelector('media-gallery'))?.slideshow?.select?.(0, undefined, { animate: false }); } catch { /* older gallery */ }
        }
      });
    }

    const active = this.imgs.find((i) => i.classList.contains('is-active')) || this.imgs[0];
    if (!active) return;
    if (active.dataset.name === name) {
      active.alt = this.alt;
      return;
    }
    const next = this.imgs.find((i) => i !== active) || active;
    next.src = url;
    next.dataset.name = name;
    try { await next.decode(); } catch { /* still swap; the browser paints when ready */ }
    if (token !== this.#token) return;
    next.alt = this.alt;
    next.removeAttribute('aria-hidden');
    if (next !== active) {
      active.alt = '';
      active.setAttribute('aria-hidden', 'true');
      if (initial || reducedMotion()) {
        next.style.transition = active.style.transition = 'none';
        nextFrame().then(() => { next.style.transition = active.style.transition = ''; });
      }
      next.classList.add('is-active');
      active.classList.remove('is-active');
    }
  }

  /** Design tiles show each design in Chrome Silver on the chosen colour. */
  async thumbs({ colour }) {
    const token = ++this.#thumbToken;
    const imgs = /** @type {HTMLImageElement[]} */ ([...this.querySelectorAll('[data-tkc-thumb]')]);
    await Promise.all(imgs.map(async (img) => {
      const { url } = await this.resolve(img.dataset.tkcThumb, FALLBACK_FINISH, colour, 400);
      if (token === this.#thumbToken && url && img.getAttribute('src') !== url) img.src = url;
    }));
  }

  /* ---------- lightbox, swipe, share, sticky bar ---------- */

  openLightbox() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-tkc-lightbox]'));
    const img = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-tkc-lightbox-img]'));
    if (!dialog || !img || !this.#currentUrl) return;
    img.src = this.#currentUrl.replace(/([?&]width=)\d+/, '$11600');
    img.alt = this.alt;
    dialog.showModal();
    track('tkc_zoom', this.state);
  }

  setupSwipe(signal) {
    if (!this.hasAttribute('data-swipeable') || !this.frame) return;
    let x0 = 0; let y0 = 0; let tracking = false;
    this.frame.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      tracking = true; this.#swiped = false; x0 = e.clientX; y0 = e.clientY;
    }, { signal, passive: true });
    this.frame.addEventListener('pointerup', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - x0; const dy = e.clientY - y0;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.#swiped = true;
      setTimeout(() => { this.#swiped = false; }, 350);
      this.cycleDesign(dx < 0 ? 1 : -1);
    }, { signal, passive: true });
    this.frame.addEventListener('pointercancel', () => { tracking = false; }, { signal, passive: true });
  }

  cycleDesign(step) {
    const keys = this.designKeys;
    const i = keys.indexOf(this.state.design);
    const next = keys[(i + step + keys.length) % keys.length];
    if (!next) return;
    if (this.product) {
      const link = this.querySelector(`a[data-tkc-design-link="${CSS.escape(next)}"]`);
      if (link) window.location.href = link.getAttribute('href') || '';
      return;
    }
    const radio = this.radios('design').find((r) => r.value === next);
    if (!radio) return;
    radio.checked = true;
    this.#done.add('design');
    this.openStep(ALL_OPEN);
    this.update();
  }

  async share() {
    const url = this.shareUrl().toString();
    const n = this.names;
    const title = `My Technikal build: ${n.design} in ${n.finish}`;
    try {
      if (navigator.share && window.matchMedia?.('(pointer: coarse)').matches) {
        await navigator.share({ title, url });
        track('tkc_share', { via: 'share' });
        return;
      }
      await navigator.clipboard.writeText(url);
      this.toast('Link copied');
      track('tkc_share', { via: 'clipboard' });
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return; // share sheet closed
      window.prompt('Copy your build link:', url); // clipboard blocked
    }
  }

  toast(text) {
    const toast = this.querySelector('[data-tkc-toast]');
    if (!toast) return;
    toast.textContent = text;
    toast.classList.add('is-on');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('is-on'), 1800);
  }

  /** What the sticky bar's button stands for: the waitlist form, else Horizon's Add to cart / the shop link. */
  ctaTarget() {
    return this.scope.querySelector('[data-tkc-waitlist]')
      || this.scope.querySelector('product-form-component')
      || this.querySelector('[data-tkc-shop]');
  }

  setupStickyBar(signal) {
    const bar = this.querySelector('[data-tkc-sticky]');
    const cta = this.ctaTarget();
    if (!bar || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    const steps = this.querySelector('[data-tkc-steps]') || this;
    let pastSteps = false; let ctaVisible = false;
    // Only once the visitor has scrolled past the choices (they sit above the screen), so the bar never covers a swatch.
    const render = () => bar.classList.toggle('is-on', pastSteps && !ctaVisible);
    this.renderSticky = render;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === steps) pastSteps = !e.isIntersecting && e.boundingClientRect.bottom < 0;
        else ctaVisible = e.isIntersecting;
      }
      render();
    }, { threshold: 0 });
    io.observe(steps);
    if (cta) io.observe(cta);
    signal.addEventListener('abort', () => io.disconnect());
  }

  stickyAction() {
    const waitlist = this.scope.querySelector('[data-tkc-waitlist]');
    if (waitlist) {
      waitlist.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      /** @type {HTMLInputElement | null} */ (waitlist.querySelector('input[type="email"]'))?.focus({ preventScroll: true });
      return;
    }
    const atc = /** @type {HTMLButtonElement | null} */ (this.scope.querySelector('product-form-component [type="submit"]'));
    if (atc && !atc.disabled) { atc.click(); return; }
    const shop = this.querySelector('a[data-tkc-shop]');
    if (shop) window.location.href = shop.getAttribute('href') || '';
  }
}

if (!customElements.get('tkc-configurator')) customElements.define('tkc-configurator', TkcConfigurator);

/* ---------- designs gallery -> configurator ---------- */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[data-tkc-pick]') : null;
  if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  const target = [...document.querySelectorAll('tkc-configurator')].find((c) => c.getAttribute('data-mode') !== 'product');
  if (!target) return; // no homepage configurator on this page: follow the link
  event.preventDefault();
  document.dispatchEvent(new CustomEvent('tkc:select', { detail: { design: link.getAttribute('data-tkc-pick') } }));
  const section = target.closest('section') || target;
  section.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  /** @type {HTMLElement | null} */ (target.querySelector('h2[tabindex]'))?.focus({ preventScroll: true });
});

/* ---------- fit checker (blocks/tkc-fit-check.liquid) ----------
   The stacking-steps behaviour comes from assets/tk-pdp.js (Solara quiz: [data-tk-quiz], first step "s-phone").
   Here: remember the answers, fill the result (preview of THEIR AirPods with the current design/finish),
   tag the "make mine next" request, and "Build yours" preselects model + colour in the configurator. */
document.addEventListener('click', (event) => {
  const el = event.target instanceof Element ? event.target : null;
  const dlg = el?.closest('[data-tkc-fit]');
  if (!el || !dlg) return;
  const tile = el.closest('.tk-q__tile');
  if (tile instanceof HTMLElement) {
    if (tile.dataset.model) dlg.setAttribute('data-model', tile.dataset.model);
    if (tile.dataset.colour) {
      dlg.setAttribute('data-colour', tile.dataset.colour);
      dlg.setAttribute('data-colour-name', tile.dataset.colourName || tile.dataset.colour);
      fillFitResult(/** @type {HTMLElement} */ (dlg));
    }
    if (tile.dataset.other) {
      const tags = dlg.querySelector('[data-tkc-fit-tags]');
      if (tags) /** @type {HTMLInputElement} */ (tags).value = `fit-request,model-${tile.dataset.other}`;
      dlg.querySelector('[data-tkc-fit-other-text]')?.toggleAttribute('hidden', tile.dataset.other !== 'other');
    }
    return;
  }
  if (el.closest('[data-tkc-fit-build]')) {
    const detail = { airpods: dlg.getAttribute('data-model'), colour: dlg.getAttribute('data-colour') };
    /** @type {HTMLDialogElement} */ (dlg).close();
    document.dispatchEvent(new CustomEvent('tkc:select', { detail }));
    const cfg = document.querySelector('tkc-configurator');
    cfg?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    track('tkc_fit_build', detail);
  }
});

async function fillFitResult(dlg) {
  const cfg = /** @type {any} */ (document.querySelector('tkc-configurator'));
  const model = dlg.getAttribute('data-model') || 'max2';
  const colour = dlg.getAttribute('data-colour') || 'midnight';
  const short = model === 'max1' ? 'AirPods Max' : 'AirPods Max 2';
  const line = dlg.querySelector('[data-tkc-fit-line]');
  if (line) line.textContent = `Your ${short} · ${dlg.getAttribute('data-colour-name') || colour}`;
  const img = /** @type {HTMLImageElement | null} */ (dlg.querySelector('[data-tkc-fit-img]'));
  if (!img) return;
  // The configurator's current design + finish (and its file lookup) when it is on the page.
  if (cfg?.resolve) {
    const { design, finish } = cfg.state;
    const { url } = await cfg.resolve(design, finish, colour, 800, [[FALLBACK_FINISH, colour], [FALLBACK_FINISH, FALLBACK_COLOUR]]);
    if (url) img.src = url;
    const caption = dlg.querySelector('[data-tkc-fit-caption]');
    if (caption) caption.textContent = `${cfg.names.design} in ${cfg.names.finish}`;
    return;
  }
  const base = dlg.getAttribute('data-file-base') || '';
  const ext = dlg.getAttribute('data-file-ext') || '.jpg';
  if (base) {
    const url = `${base}bloom-chrome-silver-${colour}${ext}?v=${IMG_VER}&width=800`;
    if (await probe(url)) img.src = url;
  }
}

// Any link to #fit-check opens the checker (ads, emails, menu).
function openFitFromHash() {
  if (window.location.hash === '#fit-check') /** @type {HTMLElement | null} */ (document.querySelector('[data-tkc-fit-open]'))?.click();
}
window.addEventListener('hashchange', openFitFromHash);
if (document.readyState === 'complete') openFitFromHash();
else window.addEventListener('load', openFitFromHash, { once: true });

// After a "make mine next" request posts, reopen the checker on its thank-you message.
for (const done of document.querySelectorAll('[data-tkc-fit] [data-tkc-fit-done]')) {
  const dlg = /** @type {HTMLDialogElement | null} */ (done.closest('dialog'));
  if (!dlg) continue;
  dlg.querySelectorAll('[data-step], [data-result-step]').forEach((s) => { /** @type {HTMLElement} */ (s).hidden = true; });
  const result = done.closest('[data-result-step]');
  if (result) /** @type {HTMLElement} */ (result).hidden = false;
  if (!dlg.open) dlg.showModal();
}

/* ---------- slow reveal on scroll ([data-tkc-reveal], CSS in tkc-chrome.css) ---------- */
const revealer = !reducedMotion() && 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
    for (const { isIntersecting, target } of entries) {
      if (!isIntersecting) continue;
      target.classList.add('is-in');
      revealer?.unobserve(target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 })
  : null;

function setupReveal(root = document) {
  for (const el of root.querySelectorAll('[data-tkc-reveal]:not([data-tkc-r])')) {
    el.setAttribute('data-tkc-r', '');
    // Already on screen (or no observer): show at once, so nothing above the fold flashes.
    if (!revealer || el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
    else revealer.observe(el);
  }
  if (revealer) document.documentElement.classList.add('tkc-motion');
}
setupReveal();
// Theme editor re-renders a section: reveal its fresh markup too.
document.addEventListener('shopify:section:load', (e) => setupReveal(/** @type {Element} */ (e.target)));

/* ---------- videos from Content > Files ([data-tkc-video]) ----------
   Until a file is uploaded its URL 404s: drop the <video> so the image underneath shows. Media errors don't
   bubble, so listen in the capture phase; also catch videos that failed before this module ran.
   Reduced motion: no autoplay loops. */
function dropBrokenVideo(video) {
  if (video instanceof HTMLVideoElement && video.matches('[data-tkc-video]')) video.remove();
}
/** Images that name a Content > Files image fall back to the theme placeholder until that file exists. */
function fallbackImage(img) {
  if (!(img instanceof HTMLImageElement) || !img.dataset.tkcFallback || img.src.endsWith(img.dataset.tkcFallback)) return;
  img.removeAttribute('srcset');
  img.src = img.dataset.tkcFallback;
}
document.addEventListener('error', (e) => { dropBrokenVideo(e.target); fallbackImage(e.target); }, true);
for (const img of document.querySelectorAll('img[data-tkc-fallback]')) {
  const i = /** @type {HTMLImageElement} */ (img);
  if (i.complete && i.naturalWidth === 0) fallbackImage(i);
}
for (const video of document.querySelectorAll('video[data-tkc-video]')) {
  const v = /** @type {HTMLVideoElement} */ (video);
  if (v.error || v.networkState === 3) dropBrokenVideo(v);
  else if (reducedMotion()) {
    v.removeAttribute('autoplay');
    v.pause();
  }
}
